"use client";

/**
 * Background Call Indicator
 * Floating badge that shows when phone calls are running in the background
 */

import { useEffect, useState } from "react";
import { useAtomValue } from "jotai";
import { useTranslations } from "next-intl";
import { Phone, X } from "lucide-react";
import { cn } from "@lib/utils/utils";
import { Button } from "@components/ui/button";
import {
  backgroundCallsAtom,
  hasBackgroundCallsAtom,
  type BackgroundCall,
} from "@/frontend/lib/atoms/core/background-calls";

interface BackgroundCallIndicatorProps {
  onOpenCall?: (call: BackgroundCall) => void;
  onDismiss?: (testCallId: string) => void;
}

function formatElapsedTime(startedAt: Date): string {
  const now = new Date();
  const elapsed = Math.floor((now.getTime() - startedAt.getTime()) / 1000);
  const minutes = Math.floor(elapsed / 60);
  const seconds = elapsed % 60;
  return `${minutes}:${seconds.toString().padStart(2, "0")}`;
}

function CallStatusBadge({ status }: { status: string | null }) {
  const t = useTranslations();
  const statusConfig: Record<string, { label: string; className: string }> = {
    pending: { label: t("agent.test.status.pending"), className: "bg-gray-500" },
    registered: { label: t("agent.test.status.registered"), className: "bg-blue-500" },
    ongoing: { label: t("agent.test.status.ongoing"), className: "bg-green-500 animate-pulse" },
    ended: { label: t("agent.test.status.ended"), className: "bg-gray-500" },
  };
  const config = statusConfig[status || "pending"] || statusConfig.pending;
  return <span className={cn("rounded-full px-2 py-0.5 text-xs text-white", config.className)}>{config.label}</span>;
}

export function BackgroundCallIndicator({ onOpenCall, onDismiss }: BackgroundCallIndicatorProps) {
  const t = useTranslations();
  const hasBackgroundCalls = useAtomValue(hasBackgroundCallsAtom);
  const backgroundCalls = useAtomValue(backgroundCallsAtom);
  const [elapsedTimes, setElapsedTimes] = useState<Record<string, string>>({});
  useEffect(() => {
    if (!hasBackgroundCalls) return;
    const updateTimes = () => {
      const times: Record<string, string> = {};
      backgroundCalls.forEach((call) => {
        times[call.testCallId] = formatElapsedTime(call.startedAt);
      });
      setElapsedTimes(times);
    };
    updateTimes();
    const interval = setInterval(updateTimes, 1000);
    return () => clearInterval(interval);
  }, [hasBackgroundCalls, backgroundCalls]);
  if (!hasBackgroundCalls) {
    return null;
  }
  return (
    <div className="fixed right-4 bottom-4 z-50 flex flex-col gap-2">
      {backgroundCalls.map((call) => (
        <div key={call.testCallId} className="bg-background flex items-center gap-3 rounded-lg border p-3 shadow-lg">
          <div className="bg-primary/10 flex h-10 w-10 items-center justify-center rounded-full">
            <Phone className="text-primary h-5 w-5 animate-pulse" />
          </div>
          <div className="flex flex-col">
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium">{call.agentName}</span>
              <CallStatusBadge status={call.callStatus} />
            </div>
            <div className="text-muted-foreground flex items-center gap-2 text-xs">
              <span>{call.phaseName}</span>
              <span>•</span>
              <span className="font-mono">{elapsedTimes[call.testCallId] || "0:00"}</span>
            </div>
          </div>
          <div className="ml-2 flex items-center gap-1">
            {onOpenCall && (
              <Button variant="ghost" size="sm" onClick={() => onOpenCall(call)} className="h-8 px-2 text-xs">
                {t("agent.test.backgroundCall.open")}
              </Button>
            )}
            {onDismiss && (
              <Button variant="ghost" size="icon" onClick={() => onDismiss(call.testCallId)} className="h-8 w-8">
                <X className="h-4 w-4" />
              </Button>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}
